import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { toast, Toaster } from "sonner";
import logo from "@/assets/images/logo.png";
import { Menu } from "lucide-react";
import { formService } from "@/services";
import DeceasedUserCard from "@/components/DeceasedUserCard";
import Footer from "@/components/Footer";

const DeceasedUserView = () => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [activeTab, setActiveTab] = useState("form");
  const [formData, setFormData] = useState(null);
  const [formName, setFormName] = useState("");
  const { hash_id: hashId } = useParams();
  const navigate = useNavigate();
  const mainContentRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [skippedFields, setSkippedFields] = useState({});

  useEffect(() => {
    const fetchDeceasedUserData = async () => {
      if (!hashId) {
        setError("No hash ID provided");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // console.log('Hash ID:', hashId);

        const response = await api.post(`/DeceasedData?hash_id=${hashId}`);

        if (response.status === 200) {
          //   console.log("response.data", response.data);

          // Extract the user data from the response
          const user = response.data.data.user;

          // Get the form ID - either from submission or use default form ID (1)
          let formId = 1; // Default form ID
          let formSubmission = null;

          if (
            response.data.data.form_submissions &&
            response.data.data.form_submissions.length > 0
          ) {
            formSubmission = response.data.data.form_submissions[0];
            formId = formSubmission.form.id;
            // Set form name from submission
            setFormName(formSubmission.form.name || "Deceased User Form");
          }

          try {
            // Fetch the form structure
            const formResponse = await formService.getForm(formId);

            if (formResponse?.data) {
              const form = formResponse.data;
              setFormData(form);
              console.log("Form data retrieved:", form);

              // If we don't have a form name from submission, use the one from form data
              if (!formSubmission) {
                setFormName(form.name || "Deceased User Form");
              }

              // Process submission values if they exist
              const processedValues = {};
              const newSkippedFields = {};

              if (
                formSubmission &&
                Array.isArray(formSubmission.submission_values)
              ) {
                formSubmission.submission_values.forEach((item) => {
                  if (!processedValues[item.section]) {
                    processedValues[item.section] = [];
                  }

                  while (
                    processedValues[item.section].length <=
                    (item.section_repeat_index || 0)
                  ) {
                    processedValues[item.section].push({});
                  }

                  // Track skipped fields
                  if (item.skip === 1) {
                    const key = `${item.section}_${item.field_id}_${
                      item.section_repeat_index || 0
                    }`;
                    newSkippedFields[key] = true;
                  }

                  // Special handling for file fields
                  if (
                    typeof item.value === "string" &&
                    item.value.includes("https://")
                  ) {
                    processedValues[item.section][
                      item.section_repeat_index || 0
                    ][item.field_id] = item.value;
                  } else {
                    processedValues[item.section][
                      item.section_repeat_index || 0
                    ][item.field_id] = item.value;
                  }
                });
              } else {
                // Initialize empty values for all sections
                form.form_form_steps.forEach((step) => {
                  step.form_step_form_sections.forEach((section) => {
                    processedValues[section.id] = [{}]; // Initialize with one empty row
                  });
                });
              }

              setSkippedFields(newSkippedFields);
              setFormValues(processedValues);
            }
          } catch (formError) {
            console.error("Error fetching form data:", formError);
          }

          setUserData({ user });
        } else {
          setError("Failed to load user data");
        }
      } catch (err) {
        console.error("Error fetching deceased user data:", err);
        setError(
          err.response?.data?.message ||
            "An error occurred while fetching the user data"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeceasedUserData();
  }, [hashId]);

  const formatFieldValue = (field, value) => {
    if (value === undefined || value === null || value === "") {
      return "Not provided";
    }

    if (field.field_type === "date") {
      // Format date value
      try {
        const date = new Date(value);
        return date.toLocaleDateString("en-GB");
      } catch (e) {
        return value;
      }
    }

    if (field.field_type === "checkbox") {
      return value === "1" ? "Yes" : "No";
    }

    if (field.field_type === "radio" && field.options) {
      try {
        const options = JSON.parse(field.options);
        const option = options.find((opt) => opt.value === value);
        return option ? option.label : value;
      } catch (e) {
        return value;
      }
    }

    if (field.field_type === "file") {
      return (
        <div className="flex items-center justify-between">
          <div className="text-gray-900">
            {value ? value.split("/").pop() : "Not provided"}
          </div>
          {value && (
            <button
              onClick={() => window.open(value, "_blank")}
              className="px-3 py-1 text-sm bg-[#F16745] text-white rounded hover:bg-[#F16745]/90 transition-colors"
            >
              View File
            </button>
          )}
        </div>
      );
    }

    return value;
  };

  const scrollToTop = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep, activeTab]);

  // Handle navigation
  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
    scrollToTop();
  };

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
    scrollToTop();
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center min-h-screen bg-[#F1EBDB]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F16745]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F1EBDB] p-6">
        <img src={logo} alt="PostScrypt Logo" className="h-16 w-auto mb-8" />
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              ></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Error Loading Data
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2.5 bg-[#F16745] text-white font-medium rounded-lg hover:bg-[#E55D3D] transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F1EBDB] p-6">
        <img src={logo} alt="PostScrypt Logo" className="h-16 w-auto mb-8" />
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 mb-6">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              ></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            No User Data Found
          </h2>
          <p className="text-gray-600 mb-6">
            We couldn&apos;t find any information for the provided reference.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2.5 bg-[#F16745] text-white font-medium rounded-lg hover:bg-[#E55D3D] transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Toaster position="top-right" />

      <div className="flex w-full">
        {/* Sidebar */}
        <aside
          className={`fixed top-0 left-0 w-full sm:w-80 bg-white shadow-xl h-screen border-r border-2 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 z-[60]`}
        >
          <div className="p-6">
            <img
              src={logo}
              alt="Post Scrypt Logo"
              className="h-9 mb-6 w-auto cursor-pointer"
              onClick={() => navigate("/")}
            />
            <h2 className="text-xl font-semibold text-[#F16745] mb-6">
              {formName || "Deceased User Data"}
            </h2>
            <nav>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {
                      setActiveTab("info");
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors ${
                      activeTab === "info"
                        ? "bg-[#F1EBDB] text-[#F16745] ring-2 ring-[#F16745] ring-opacity-50"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5 ${
                        activeTab === "info"
                          ? "bg-[#F1EBDB] text-[#F16745] border-2 border-[#F16745]"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                    </span>
                    <span className="font-medium leading-tight">
                      User Overview
                    </span>
                  </button>
                </li>

                {formData && formData.form_form_steps ? (
                  // If we have the form structure, use the step titles from there
                  formData.form_form_steps.map((step, index) => (
                    <li key={step.id}>
                      <button
                        onClick={() => {
                          setActiveTab("form");
                          setCurrentStep(index);
                          scrollToTop();
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-3 rounded-lg text-sm transition-colors ${
                          activeTab === "form" && index === currentStep
                            ? "bg-[#F1EBDB] text-[#F16745] ring-2 ring-[#F16745] ring-opacity-50"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full mr-3 text-sm mt-0.5
                          ${
                            activeTab === "form" && index === currentStep
                              ? "bg-[#F1EBDB] text-[#F16745] border-2 border-[#F16745]"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="font-medium leading-tight">
                          {step.title}
                        </span>
                      </button>
                    </li>
                  ))
                ) : (
                  <li>
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No form steps found
                    </div>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main
          ref={mainContentRef}
          className="flex-1 lg:ml-80"
          style={{ backgroundColor: "#F1EBDB" }}
        >
          <div className="p-8 pt-16 md:pt-10">
            {activeTab === "info" ? (
              <div className="space-y-8">
                <DeceasedUserCard
                  userData={userData}
                  onViewFormData={() => {
                    setActiveTab("form");
                    setCurrentStep(0);
                    scrollToTop();
                  }}
                />
              </div>
            ) : (
              <div>
                {/* Step Title Card - Matching DynamicForm styling */}
                <div className="max-w-3xl mb-6 mx-auto bg-white rounded-xl shadow-sm p-8">
                  <h2 className="text-2xl font-bold text-[#F16745] mb-4">
                    {formData?.form_form_steps[currentStep]?.title ||
                      "Form Data"}
                  </h2>
                  {formData.form_form_steps[currentStep].description && (
                    <div className="text-gray-600">
                      {formData.form_form_steps[currentStep].description
                        .split("/n")
                        .map((line, index) => (
                          <React.Fragment key={index}>
                            <p className={`${index > 0 ? "mt-1" : ""}`}>
                              {line}
                            </p>
                          </React.Fragment>
                        ))}
                    </div>
                  )}
                </div>

                {/* Form Data Card - Matching DynamicForm styling */}
                <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8">
                  {formData?.form_form_steps[
                    currentStep
                  ]?.form_step_form_sections.map((section) => (
                    <div key={section.id} className="mb-8">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {section.name}
                          </h3>
                          {section.description && (
                            <p className="text-gray-600 text-sm">
                              {section.description}
                            </p>
                          )}
                        </div>
                      </div>

                      {(formValues[section.id]?.length > 0
                        ? formValues[section.id]
                        : [{}]
                      ).map((rowInstance, index) => (
                        <div key={index} className="rounded-lg mb-6">
                          <div className="grid grid-cols-1 gap-6">
                            {section.form_step_form_fields.map((field) => (
                              <div key={field.id} className="space-y-2">
                                {field.field_type === "heading" ? (
                                  <h4 className="text-lg font-semibold text-gray-900">
                                    {field.label || field.name}
                                  </h4>
                                ) : (
                                  <>
                                    <div className="flex items-center space-x-2 max-w-2xl">
                                      <label className="block font-medium text-gray-700 w-auto">
                                        {field.name}
                                        {field.required === 1 && (
                                          <span className="text-red-500 ml-1">
                                            *
                                          </span>
                                        )}
                                      </label>
                                    </div>
                                    <div className="mt-1">
                                      {skippedFields[
                                        `${section.id}_${field.id}_${index}`
                                      ] ? (
                                        <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                                          <div className="text-gray-600 italic">
                                            This question has been skipped
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                                          {field.field_type === "checkbox" ? (
                                            <div className="flex items-center">
                                              <input
                                                type="checkbox"
                                                checked={
                                                  rowInstance?.[field.id] ===
                                                  "1"
                                                }
                                                disabled
                                                className="h-5 w-5 rounded border-gray-300 text-[#F16745]"
                                              />
                                              <span className="ml-3 text-sm text-gray-600">
                                                {rowInstance?.[field.id] === "1"
                                                  ? "Yes"
                                                  : "No"}
                                              </span>
                                            </div>
                                          ) : field.field_type === "radio" ? (
                                            <div className="text-gray-900">
                                              {(field.options &&
                                                JSON.parse(field.options).find(
                                                  (opt) =>
                                                    opt.value ===
                                                    rowInstance?.[field.id]
                                                )?.label) ||
                                                rowInstance?.[field.id] ||
                                                "Not provided"}
                                            </div>
                                          ) : field.field_type === "file" ? (
                                            <div className="flex items-center justify-between">
                                              <div className="text-gray-900">
                                                {rowInstance?.[field.id]
                                                  ? rowInstance[field.id]
                                                      .split("/")
                                                      .pop()
                                                      .replace(/^\d+_/, "")
                                                  : "Not provided"}
                                              </div>
                                              {rowInstance?.[field.id] && (
                                                <button
                                                  onClick={() =>
                                                    window.open(
                                                      rowInstance[field.id],
                                                      "_blank"
                                                    )
                                                  }
                                                  className="px-3 py-1 text-sm bg-[#F16745] text-white rounded hover:bg-[#F16745]/90 transition-colors"
                                                >
                                                  View File
                                                </button>
                                              )}
                                            </div>
                                          ) : (
                                            <div className="text-gray-900">
                                              {rowInstance?.[field.id] ||
                                                "Not provided"}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Navigation Buttons - Matching DynamicForm styling */}
                  <div className="mt-8 flex justify-between pt-6">
                    {currentStep > 0 && (
                      <button
                        type="button"
                        onClick={handlePrevious}
                        className="px-6 py-2.5 border border-[#F16745] text-sm font-medium rounded-lg text-[#F16745] bg-white hover:bg-[#F1EBDB] transition duration-150"
                      >
                        ← Previous
                      </button>
                    )}

                    <div className="flex-1"></div>

                    {currentStep < formData?.form_form_steps?.length - 1 && (
                      <button
                        type="button"
                        onClick={handleNext}
                        className="ml-2 bg-[#F16745] px-6 py-2.5 text-sm font-medium rounded-lg text-white hover:bg-[#ee623f] transition duration-150"
                      >
                        Next →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 lg:ml-[70px]">
              <Footer />
            </div>
          </div>
        </main>

        {/* Mobile menu button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="fixed z-[70] bottom-4 right-4 lg:hidden bg-[#F16745] text-white p-3 rounded-full shadow-lg"
        >
          <Menu size={24} />
        </button>

        {/* Overlay for mobile */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[55] lg:hidden transition-opacity duration-300 ease-in-out"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DeceasedUserView;
